import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './MigrantQuestionnaires.css';

/**
 * District-wide view of the DESE Title I.C questionnaires.
 *
 * The point of this screen is the follow-up column. The state requires the
 * questionnaire from every student, and any "yes" answer means migrant program staff
 * should contact the family — so the two questions staff actually need answered are
 * "who said yes?" and "who has not answered yet?". Both are filters here rather than
 * something to reconstruct by opening students one at a time.
 *
 * Answers are shown in the language the parent used, because the three official
 * revisions word the questions and group the work differently.
 */
const LANGUAGE_NAMES = { en: 'English', es: 'Español', fr: 'Français' };

const MigrantQuestionnaires = () => {
  const [students, setStudents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all | follow_up | outstanding | completed
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState([]); // student_intake_ids, submitted only
  const [exporting, setExporting] = useState('');
  const [districtGaps, setDistrictGaps] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get('/api/migrant-questionnaire/district');
      setStudents(res.data.students || []);
      setSummary(res.data.summary || null);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load questionnaires.');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * The county-district code and migrant contact are printed in the header of every
   * questionnaire and are read live at PDF time, so filling them in fixes the forms
   * already submitted too. Until they are set, the whole stack prints with two blank
   * header fields - worth saying here, where the printing happens, rather than leaving
   * someone to discover it after a print run of a few hundred.
   */
  const loadDistrict = useCallback(async () => {
    try {
      const res = await axios.get('/api/districts/current');
      const d = res.data.district || res.data || {};
      setDistrictGaps({
        code: !String(d.county_district_code || '').trim(),
        contact: !String(d.migrant_contact || '').trim()
      });
    } catch (e) {
      setDistrictGaps(null); // never block the list over this
    }
  }, []);

  useEffect(() => { load(); loadDistrict(); }, [load, loadDistrict]);

  const openPdf = async (submissionId) => {
    setBusyId(submissionId);
    try {
      const res = await axios.post(
        '/api/migrant-questionnaire/generate-pdf',
        { submissionId },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert(e.response?.data?.error || 'Could not open the questionnaire PDF.');
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Bulk download. Selection wins when the user has ticked rows; otherwise the export
   * follows what is on screen, so "filter to follow-up, hit export" does the obvious
   * thing without also making them tick 30 boxes.
   */
  const runExport = async (format) => {
    const scoped = selected.length > 0;
    const body = { format };
    if (scoped) body.studentIntakeIds = selected;
    else if (filter === 'follow_up') body.only_follow_up = true;

    setExporting(format);
    try {
      const res = await axios.post('/api/migrant-questionnaire/batch-export', body, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'zip' ? 'migrant_questionnaires.zip' : 'migrant_questionnaires.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      // The error body arrives as a Blob because we asked for one
      let msg = 'Could not export questionnaires.';
      try {
        const text = await e.response?.data?.text?.();
        if (text) msg = JSON.parse(text).error || msg;
      } catch (_) { /* keep the generic message */ }
      alert(msg);
    } finally {
      setExporting('');
    }
  };

  /**
   * The CSV is deliberately the WHOLE district list, including students who have not
   * submitted - the outstanding ones are the point of a tracking spreadsheet. It sits
   * apart from the two PDF buttons, whose scope is the selection, so the difference is
   * visible rather than implied.
   */
  const downloadCsv = async () => {
    setExporting('csv');
    try {
      const res = await axios.get('/api/migrant-questionnaire/district.csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'migrant_questionnaires.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert('Could not download the CSV.');
    } finally {
      setExporting('');
    }
  };

  const visible = students.filter((s) => {
    if (filter === 'follow_up' && !s.needsFollowUp) return false;
    if (filter === 'outstanding' && s.submissionId) return false;
    if (filter === 'completed' && !s.submissionId) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      if (!`${s.studentName} ${s.grade} ${s.parentUsername}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const yn = (v) => (v === true ? 'Yes' : v === false ? 'No' : '—');

  // Only submitted questionnaires can be exported — there is no PDF for the rest.
  const exportable = visible.filter((s) => s.submissionId);
  const allChecked = exportable.length > 0 && exportable.every((s) => selected.includes(s.studentIntakeId));

  const toggleRow = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = () =>
    setSelected(allChecked ? [] : exportable.map((s) => s.studentIntakeId));

  const exportLabel = selected.length > 0
    ? `${selected.length} selected`
    : filter === 'follow_up'
      ? 'all flagged for follow-up'
      : `all ${exportable.length} submitted`;

  return (
    <div className="mqa">
      <h2>Migrant Questionnaires</h2>
      <p className="mqa-intro">
        Missouri DESE requires the Title I.C Migrant Education Parent Questionnaire from every student
        each year. A <strong>follow-up</strong> flag means the family answered yes to at least one
        question, so migrant program staff should contact them. PDFs are the official MO 500-3129 and
        open in a new tab for printing or mailing.
      </p>

      {error && <div className="mqa-error">{error}</div>}

      {districtGaps && (districtGaps.code || districtGaps.contact) && (
        <div className="mqa-notice">
          <strong>
            {districtGaps.code && districtGaps.contact
              ? 'Two header fields are not filled in yet.'
              : 'A header field is not filled in yet.'}
          </strong>{' '}
          Every questionnaire prints with{' '}
          {[districtGaps.code && 'County-District Code', districtGaps.contact && 'District Migrant Contact']
            .filter(Boolean)
            .join(' and ')}{' '}
          left blank. Setting {districtGaps.code && districtGaps.contact ? 'them' : 'it'} under{' '}
          <Link to="/district-admin/settings">School Info</Link> also corrects the questionnaires
          already submitted, because the PDFs read these values when they are generated.
        </div>
      )}

      {summary && (
        <div className="mqa-tiles">
          <button type="button" className={`mqa-tile ${filter === 'all' ? 'is-active' : ''}`} onClick={() => setFilter('all')}>
            <span className="mqa-tile-num">{summary.total}</span>
            <span className="mqa-tile-label">Students</span>
          </button>
          <button type="button" className={`mqa-tile is-followup ${filter === 'follow_up' ? 'is-active' : ''}`} onClick={() => setFilter('follow_up')}>
            <span className="mqa-tile-num">{summary.needsFollowUp}</span>
            <span className="mqa-tile-label">Need follow-up</span>
          </button>
          <button type="button" className={`mqa-tile ${filter === 'outstanding' ? 'is-active' : ''}`} onClick={() => setFilter('outstanding')}>
            <span className="mqa-tile-num">{summary.outstanding}</span>
            <span className="mqa-tile-label">Not yet submitted</span>
          </button>
          <button type="button" className={`mqa-tile ${filter === 'completed' ? 'is-active' : ''}`} onClick={() => setFilter('completed')}>
            <span className="mqa-tile-num">{summary.completed}</span>
            <span className="mqa-tile-label">Completed</span>
          </button>
        </div>
      )}

      <div className="mqa-controls">
        <input
          type="text"
          placeholder="Search student, grade or parent…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="button" className="btn-secondary" onClick={load} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={downloadCsv}
          disabled={!!exporting || students.length === 0}
          title="Every student, submitted or not, with their answers and Foster/Homeless/Migrant/Runaway flags"
        >
          {exporting === 'csv' ? 'Building CSV…' : `Download CSV (all ${students.length} students)`}
        </button>
      </div>

      {exportable.length > 0 && (
        <div className="mqa-export">
          <span className="mqa-export-scope">
            Export <strong>{exportLabel}</strong>
            {selected.length > 0 && (
              <button type="button" className="mqa-clear" onClick={() => setSelected([])}>
                clear
              </button>
            )}
          </span>
          <div className="mqa-export-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => runExport('merged')}
              disabled={!!exporting}
            >
              {exporting === 'merged' ? 'Building PDF…' : 'Download one PDF (for printing)'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => runExport('zip')}
              disabled={!!exporting}
            >
              {exporting === 'zip' ? 'Building zip…' : 'Download zip (one file per student)'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="mqa-loading">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="mqa-empty">No students match this view.</div>
      ) : (
        <div className="mqa-table-wrap">
          <table className="data-table mqa-table">
            <thead>
              <tr>
                <th className="mqa-check">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={toggleAll}
                    disabled={exportable.length === 0}
                    aria-label="Select all submitted questionnaires in this view"
                  />
                </th>
                <th>Student</th>
                <th>Grade</th>
                <th>Status</th>
                <th>Lang</th>
                <th title="Moved into the district in the past 3 years">Moved</th>
                <th title="Worked in agriculture or fishing">Ag/fishing</th>
                <th title="Plans such work soon">Plans</th>
                <th>Work reported</th>
                <th>Follow-up</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((s) => {
                const work = [...(s.jobTypeLabels || []), s.jobTypesOther].filter(Boolean);
                return (
                  <tr key={s.studentIntakeId} className={s.needsFollowUp ? 'mqa-row-followup' : ''}>
                    <td className="mqa-check">
                      <input
                        type="checkbox"
                        checked={selected.includes(s.studentIntakeId)}
                        onChange={() => toggleRow(s.studentIntakeId)}
                        disabled={!s.submissionId}
                        aria-label={`Select ${s.studentName || 'student'} for export`}
                      />
                    </td>
                    <td>{s.studentName || '—'}</td>
                    <td>{s.grade || '—'}</td>
                    <td>
                      {s.submittedAt
                        ? <span className="mqa-done">{new Date(s.submittedAt).toLocaleDateString()}</span>
                        : <span className="mqa-outstanding">Not submitted</span>}
                    </td>
                    <td>{s.language ? (LANGUAGE_NAMES[s.language] || s.language) : '—'}</td>
                    <td>{yn(s.movedIntoDistrict)}</td>
                    <td>{yn(s.workedAgFishing)}</td>
                    <td>{yn(s.plansAgFishing)}</td>
                    <td className="mqa-work">{work.length ? work.join('; ') : '—'}</td>
                    <td>{s.needsFollowUp ? <span className="mqa-flag">Yes</span> : '—'}</td>
                    <td>
                      {s.submissionId ? (
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => openPdf(s.submissionId)}
                          disabled={busyId === s.submissionId}
                        >
                          {busyId === s.submissionId ? 'Opening…' : 'View PDF'}
                        </button>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MigrantQuestionnaires;
